import type { RouteConfig } from "@asteasolutions/zod-to-openapi";
import { defaultErrorResponses, problemResponse, registry } from "../registry.js";
import { z } from "../zod.js";
import {
  AdjustmentRequestAcceptedResponse,
  AdjustmentRequestCancelRequest,
  AdjustmentRequestCreateRequest,
  AdjustmentRequestDecideRequest,
  AdjustmentRequestDetailResponse,
  AdjustmentRequestListResponse,
  AtpResponse,
  CountApproveResponse,
  CountCancelRequest,
  CountLineAddRequest,
  CountLineCountRequest,
  CountLineReviewRequest,
  CountScheduleCreateRequest,
  CountScheduleListResponse,
  CountSchedulePatchRequest,
  CutRequest,
  CutResponse,
  CycleCountCreateRequest,
  CycleCountDetailResponse,
  CycleCountListResponse,
  CycleCountSchedule,
  InventorySettingsPatchRequest,
  InventorySettingsResponse,
  InventoryLocation,
  InventoryLot,
  LevelsResponse,
  LookupResponse,
  LocationContentsResponse,
  LocationQrLabelsRequest,
  LocationQrLabelsResponse,
  LocationCreateRequest,
  LocationListResponse,
  LocationPatchRequest,
  LotCreateRequest,
  LotDetailResponse,
  LotListResponse,
  LotQcStatusResponse,
  LotQuarantineRequest,
  LotReleaseRequest,
  MovementCreateRequest,
  MovementCreateResponse,
  MovementListResponse,
  MovementReverseRequest,
  MovementReverseResponse,
  ReceiptCreateRequest,
  ReceiptCreateResponse,
  RemeasureRequest,
  RemeasureResponse,
  ReservationCreateRequest,
  ReservationCreateResponse,
  ReservationListResponse,
  ReservationReleaseResponse,
  ScanRequest,
  ScanResponse,
  StockUnit,
  StockUnitCardResponse,
  StockUnitHistoryResponse,
  StockUnitListResponse,
  StockUnitRegisterRequest,
  TraceResponse,
  TransferCancelRequest,
  TransferCreateRequest,
  TransferDetailResponse,
  TransferListResponse,
  TransferPatchRequest,
  TransferReceiveRequest,
  TransferReceiveResponse,
  UnitQuarantineRequest,
  UnitReleaseRequest,
  UnitRetireRequest,
  UnitStatusResponse,
} from "./schemas.js";

// Module 06 — inventory HTTP surface, PHASES 2a + 2b + 2c + 2d (spec 06 §5):
// the locations tree (§5 rows 1–4), the on-hand grid (row 22), the movement
// ledger (rows 24–26), lots + qc status + trace (rows 6–11), stock units +
// history + hold/release/retire (rows 12–15, 18–19), composite receipts
// (row 27), the 2c measured-unit core — cut + remeasure (rows 16–17),
// scan (row 20), lookup (row 21) and ATP (row 23) — the 2d
// reservations (rows 28–30) + transfers (rows 31–36) — and the 2e cycle
// counts (rows 37–46), adjustment requests (row 48, incl. the POST /movements
// 202 value-gate branch, D-046 pt 1 closed) and inventory settings (row 49).
// Mirrors onboarding/register.ts (the `route()` helper; Idempotency-Key on
// every mutation; RFC 9457 problem responses). Row 5 (locations qr-labels PDF)
// is LIVE as of the module-02 QR print-kit slice (Phase 5b) — the route is
// module 06's, the renderer is module 02's, reached through LABEL PRINT PORT. There is NO
// public POST for genealogy edges: §2.9 — edges are created by consumption/
// output/cut/reprocess flows, never hand-entered.

const sessionSecurity = [{ sessionCookie: [] }];
const idempotencyHeader = { $ref: "#/components/parameters/IdempotencyKey" };

function route(config: {
  method: "get" | "post" | "patch" | "put" | "delete";
  path: string;
  operationId: string;
  summary: string;
  permission?: string;
  request?: RouteConfig["request"];
  responses: RouteConfig["responses"];
  description?: string;
}): void {
  const mutation = config.method !== "get";
  registry.registerPath({
    method: config.method,
    path: config.path,
    operationId: config.operationId,
    tags: ["inventory"],
    summary: config.summary,
    description:
      (config.description ?? "") +
      (config.permission !== undefined ? ` Requires \`${config.permission}\`.` : ""),
    security: sessionSecurity,
    ...(config.request !== undefined ? { request: config.request } : {}),
    ...(mutation ? { parameters: [idempotencyHeader] } : {}),
    responses: { ...defaultErrorResponses(), ...config.responses },
  });
}

const jsonBody = (schema: z.ZodType) => ({
  body: { content: { "application/json": { schema } } },
});

const ok = (description: string, schema: z.ZodType) => ({
  200: { description, content: { "application/json": { schema } } },
});

const created = (description: string, schema: z.ZodType) => ({
  201: { description, content: { "application/json": { schema } } },
});

const IdParam = z.object({ id: z.guid() });
const cursorQuery = {
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
};

// ===========================================================================
// locations (spec 06 §2.1, §5 rows 1–4)
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/locations",
  operationId: "listInventoryLocations",
  summary: "List locations (tree order by materialized path; cursor page)",
  permission: "inventory.read",
  description:
    "Ordered by (path, id) so a page walk reconstructs the warehouse→zone→bin tree. Facility-scoped roles see only their facilities' locations (service-layer filter, D-043). `q` matches code, name AND materialized path, so a bin is findable by the path a storekeeper reads off a rack label. `id` may be repeated to resolve a KNOWN, BOUNDED set of locations (an id→path label map for a page of movements or count lines): when it is present and `limit` is omitted, the limit defaults to the number of ids requested (capped at 200) rather than to 50, so a bounded lookup can never come back silently short. `is_countable` narrows to the locations a cycle count may scope over.",
  request: {
    query: z.object({
      facility_id: z.guid().optional(),
      id: z.array(z.guid()).max(200).optional(),
      kind: z.string().optional(),
      parent_id: z.guid().optional(),
      q: z.string().optional(),
      is_active: z.coerce.boolean().optional(),
      is_countable: z.coerce.boolean().optional(),
      ...cursorQuery,
    }),
  },
  responses: ok("A page of locations", LocationListResponse),
});
route({
  method: "post",
  path: "/v1/inventory/locations",
  operationId: "createInventoryLocation",
  summary: "Create a physical location (warehouse, zone or bin)",
  permission: "inventory.location.manage",
  description:
    "Physical kinds only — the 8 virtual kinds are system-provisioned per facility (idempotently, on first location write) and cannot be created manually (spec 06 §2.1). Hierarchy is service-enforced: warehouse is a root, zone under warehouse, bin under zone or warehouse. qr_payload is minted at creation (id-stable, platform QR canon). Emits inventory.location_updated.",
  request: jsonBody(LocationCreateRequest),
  responses: {
    ...created("The created location", InventoryLocation),
    404: problemResponse("FACILITY_NOT_FOUND"),
    409: problemResponse("LOCATION_CODE_TAKEN — UNIQUE(tenant, facility, code)"),
    422: problemResponse(
      "LOCATION_HIERARCHY_INVALID — parent-kind rule violated / VIRTUAL_LOCATION_IMMUTABLE — virtual kinds are system-provisioned",
    ),
  },
});
route({
  method: "patch",
  path: "/v1/inventory/locations/{id}",
  operationId: "patchInventoryLocation",
  summary: "Rename / re-parent / toggle a location",
  permission: "inventory.location.manage",
  description:
    "Re-parenting recomputes the subtree's materialized paths under a per-facility advisory lock (bounded acquire → 503 RESOURCE_BUSY). Virtual locations are immutable EXCEPT allow_negative on production kind (spec 06 §2.1/§9.5); toggling allow_negative recomputes the location's stock_levels.negative_allowed rows in the same txn — turning it off while on-hand is negative is refused (409 NEGATIVE_STOCK_BLOCKED). Emits inventory.location_updated.",
  request: { params: IdParam, ...jsonBody(LocationPatchRequest) },
  responses: {
    ...ok("The updated location", InventoryLocation),
    404: problemResponse("LOCATION_NOT_FOUND (cross-tenant ids are invisible — §9.9)"),
    409: problemResponse("NEGATIVE_STOCK_BLOCKED — allow_negative off while a level is negative"),
    422: problemResponse(
      "LOCATION_HIERARCHY_INVALID / VIRTUAL_LOCATION_IMMUTABLE / ALLOW_NEGATIVE_PRODUCTION_ONLY",
    ),
    503: problemResponse("RESOURCE_BUSY — another structural location change holds the lock"),
  },
});
route({
  method: "post",
  path: "/v1/inventory/locations/qr-labels",
  operationId: "printInventoryLocationQrLabels",
  summary: "Batch QR label PDF for a set of locations",
  permission: "onboarding.generate_qr_kit",
  description:
    "Spec 06 §5 row 5. Renders a location label sheet through Module 02's QR print-kit surface (spec 02 §2.8/§3.4): the call creates a `location_labels` batch, freezes each label's lines and the owning module's `/q/loc/{uuid}` payload, and answers with a presigned PDF link, the file registry's 5-minute URL (D-322); GET /v1/qr-kits/{id} mints a fresh one. The batch is visible at GET /v1/qr-kits and satisfies the go-live checklist's channels.qr_kit_printed exactly as the wizard's kit screen does. Every named id must be a PHYSICAL, active location visible to the caller — an out-of-scope or virtual id is 404 rather than a silently shorter sheet. The grant is `onboarding.generate_qr_kit` (storekeeper + facility_admin, spec 02 §7): the same act through a different screen keeps the same permission. 501 LABEL_PRINTER_UNAVAILABLE when no print surface is deployed.",
  request: jsonBody(LocationQrLabelsRequest),
  responses: {
    ...ok("The rendered batch + presigned PDF", LocationQrLabelsResponse),
    404: problemResponse("LOCATION_NOT_FOUND — an id is absent, virtual, inactive or out of scope"),
    422: problemResponse(
      "PACK_NOT_INSTALLED / QR_LABEL_TEMPLATE_NOT_FOUND — label templates are pack data / QR_PAYLOAD_MISSING — the owning module has no scan path for a location / QR_KIT_SELECTION_EMPTY",
    ),
    501: problemResponse("LABEL_PRINTER_UNAVAILABLE — no label print surface in this deployment"),
  },
});
route({
  method: "get",
  path: "/v1/inventory/locations/{id}/contents",
  operationId: "getInventoryLocationContents",
  summary: "What's here — the location's stock_levels rows",
  permission: "inventory.read",
  request: { params: IdParam },
  responses: {
    ...ok("The location's on-hand rows", LocationContentsResponse),
    404: problemResponse("LOCATION_NOT_FOUND"),
  },
});

// ===========================================================================
// levels (spec 06 §2.5, §5 row 22)
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/levels",
  operationId: "listInventoryLevels",
  summary: "On-hand grid (derived cache; cursor page)",
  permission: "inventory.read",
  description:
    "The stock_levels cache §2.5 maintains in the same transaction as every movement. Ordered by the identity tuple (product, location, lot, stock_unit).",
  request: {
    query: z.object({
      product_id: z.guid().optional(),
      facility_id: z.guid().optional(),
      location_id: z.guid().optional(),
      lot_id: z.guid().optional(),
      nonzero: z.coerce.boolean().optional(),
      ...cursorQuery,
    }),
  },
  responses: ok("A page of on-hand rows", LevelsResponse),
});

// ===========================================================================
// movements (spec 06 §2.4, §3.9, §5 rows 24–26)
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/movements",
  operationId: "listInventoryMovements",
  summary: "The immutable ledger (cursor page, newest first)",
  permission: "inventory.read",
  description:
    "Ordered by (created_at, id) descending. unit_cost_minor/currency are field-redacted (null + redacted:true) without the inventory.cost.read grant; procurement sees cost only on purchase_order-sourced movements (spec 06 §7).",
  request: {
    query: z.object({
      movement_type: z.string().optional(),
      product_id: z.guid().optional(),
      lot_id: z.guid().optional(),
      stock_unit_id: z.guid().optional(),
      location_id: z.guid().optional(),
      source_doc_type: z.string().optional(),
      source_doc_id: z.guid().optional(),
      performed_by: z.guid().optional(),
      moved_from: z.string().optional(),
      moved_to: z.string().optional(),
      ref_work_order_id: z
        .guid()
        .optional()
        .describe(
          "Only the movements recorded against this work order (spec 06 §5 row 24, D-391 ruling 3).",
        ),
      ...cursorQuery,
    }),
  },
  responses: ok("A page of movements", MovementListResponse),
});
route({
  method: "post",
  path: "/v1/inventory/movements",
  operationId: "createInventoryMovement",
  summary: "Post a manual movement (adjustment / repack / scrap / issue)",
  permission: "inventory.adjust",
  description:
    "The single ledger write path (§2.4): resolves the product's base UoM inside the movement transaction (UomConversionService.resolveRatioExact — §2.10), computes qty_base exactly in SQL numeric (half-even at 6 dp, cross-checked against the kernel), validates the movement-type × location-kind legality matrix, locks the stock unit (FOR UPDATE, §9.1), inserts the append-only row, upserts stock_levels in the same txn (§2.5) and appends inventory.movement_recorded to the outbox. The Idempotency-Key doubles as the domain idempotency key: a replay returns the ORIGINAL movement with idempotent_replay:true; the same key with a different body is 409 IDEMPOTENCY_KEY_REUSE (§9.2). The replay answers only the user who posted the movement. Any other caller who presents the key, and that user acting through an impersonation grant, gets the same 409 with nothing from the movement (D-303). Adjustments require reason_code + a note of at least 10 characters (§3.9). THE §3.9 VALUE GATE (2e — replaces the 2a fail-closed interim, D-046 pt 1 closed): |qty_base × last captured unit cost| ≤ the tenant's adjustment_value_threshold_minor → the movement posts (201). Above the threshold, unvalued stock (edge case 14), a cost/threshold currency mismatch, or NO threshold configured (fail-closed) → nothing touches the ledger; an adjustment_requests row holds the exact payload and the response is 202 {adjustment_request_id, approval_request_id} pending a decide (D-049 local tier while Module 12 is unbound). stock_unit_id is accepted only with `repack` (§2.4 amendment).",
  request: jsonBody(MovementCreateRequest),
  responses: {
    ...created("The posted movement id", MovementCreateResponse),
    202: {
      description:
        "Over the §3.9 value threshold (or unvalued / threshold not configured): held for approval, nothing posted",
      content: { "application/json": { schema: AdjustmentRequestAcceptedResponse } },
    },
    404: problemResponse(
      "LOCATION_NOT_FOUND / PRODUCT_NOT_FOUND / LOT_NOT_FOUND / UNIT_NOT_FOUND / WORK_ORDER_NOT_FOUND — the `work_order_id` names no work order the caller can read, or module 04 is not deployed (spec 06 §3.9, D-391 ruling 3)",
    ),
    409: problemResponse(
      "NEGATIVE_STOCK_BLOCKED — on_hand would go negative (§9.5) / IDEMPOTENCY_KEY_REUSE — key already used by a different mutation (§9.2)",
    ),
    422: problemResponse(
      "MOVEMENT_ILLEGAL — legality matrix (§2.4) / LOT_NOT_RELEASED — a manual `issue` CONSUMES the lot and only released stock is consumable (§2.2, D-211 addendum); `scrap`, `repack` and `adjustment` stay open as the quarantine remedies and correction doors / UOM_NOT_CONVERTIBLE (§9.6) / MEASURE_PRECISION_LOSS (§9.7) / VALIDATION_FAILED",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/movements/{id}/reverse",
  operationId: "reverseInventoryMovement",
  summary: "Reverse a movement (append the exact opposite, linked to the original)",
  permission: "inventory.movement.reverse",
  description:
    "Posts a `reversal` movement mirroring the original's endpoints and carrying the original's qty_base VERBATIM (a conversion-factor edit between post and reversal must not unbalance the ledger — §3.9). The original row is untouched (append-only). A reversal cannot itself be reversed (422 REVERSAL_NOT_REVERSIBLE — re-post instead) and a movement reverses at most once (409 MOVEMENT_ALREADY_REVERSED). §3.9 2c rulings: cut legs (source_doc_type='cut') and the empty-container retirement scrap are NOT reversible; a re-measure adjustment IS — its reversal restores remaining_measure in the same txn, refusing on a dead unit (422 UNIT_NOT_AVAILABLE), a changed measure UoM, a unit that has since moved bins (both 422 REVERSAL_NOT_REVERSIBLE — correct forward), or a below-zero restore (409 NEGATIVE_STOCK_BLOCKED).",
  request: { params: IdParam, ...jsonBody(MovementReverseRequest) },
  responses: {
    ...created("The reversal movement id", MovementReverseResponse),
    404: problemResponse("MOVEMENT_NOT_FOUND (cross-tenant ids are invisible — §9.9)"),
    409: problemResponse(
      "MOVEMENT_ALREADY_REVERSED / NEGATIVE_STOCK_BLOCKED — reversing would strand negative stock / IDEMPOTENCY_KEY_REUSE — key already used by an unrelated mutation (§9.2)",
    ),
    422: problemResponse("REVERSAL_NOT_REVERSIBLE — a reversal cannot be reversed"),
  },
});

// ===========================================================================
// lots (spec 06 §2.2, §3.1, §5 rows 6–10) — PHASE 2b
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/lots",
  operationId: "listInventoryLots",
  summary: "List lots (cursor page, newest first)",
  permission: "inventory.read",
  description:
    "Filters include supplier_lot_code (exact — the §3.7 upstream-claim search, M06-S09) and attribute equality via attr.{key}=value query parameters (GIN-backed jsonb match).",
  request: {
    query: z.object({
      product_id: z.guid().optional(),
      qc_status: z.string().optional(),
      supplier_id: z.guid().optional(),
      origin: z.string().optional(),
      lot_code: z.string().optional(),
      supplier_lot_code: z.string().optional(),
      created_from: z.string().optional(),
      created_to: z.string().optional(),
      ...cursorQuery,
    }),
  },
  responses: ok("A page of lots", LotListResponse),
});
route({
  method: "post",
  path: "/v1/inventory/lots",
  operationId: "createInventoryLot",
  summary: "Create a manual / opening lot",
  permission: "inventory.receipt.create",
  description:
    "Origin requirements are service-enforced (§2.2): purchased requires supplier_id; produced/reprocessed require work_order_id. lot_code auto-assigns LOT-{yy}{seq} when omitted (per-tenant sequence). The product is validated through the catalogue service inside the transaction — no cross-module FK. Emits inventory.lot_created.",
  request: jsonBody(LotCreateRequest),
  responses: {
    ...created("The created lot", InventoryLot),
    404: problemResponse("PRODUCT_NOT_FOUND"),
    409: problemResponse("LOT_CODE_TAKEN — UNIQUE(tenant, product, lot_code)"),
    422: problemResponse("LOT_STATE_INVALID — origin requirement violated (§2.2)"),
  },
});
route({
  method: "get",
  path: "/v1/inventory/lots/{id}",
  operationId: "getInventoryLot",
  summary: "Lot detail: row + stock summary by location + units",
  permission: "inventory.read",
  request: { params: IdParam },
  responses: {
    ...ok("The lot with its stock summary and units", LotDetailResponse),
    404: problemResponse("LOT_NOT_FOUND (cross-tenant ids are invisible — §9.9)"),
  },
});
route({
  method: "post",
  path: "/v1/inventory/lots/{id}/quarantine",
  operationId: "quarantineInventoryLot",
  summary: "QC hold a lot — STATUS-ONLY, never a movement (§2.4)",
  permission: "inventory.quarantine",
  description:
    "Sets qc_status='quarantined', excluding the lot's stock from ATP/lookup wherever it physically sits; NO movement is posted (spec 06 §2.4 quarantine clarifications). Legal from pending/released. Emits inventory.lot_quarantined with affected_qty_base + affected_unit_ids.",
  request: { params: IdParam, ...jsonBody(LotQuarantineRequest) },
  responses: {
    ...ok("The resulting qc_status", LotQcStatusResponse),
    404: problemResponse("LOT_NOT_FOUND"),
    422: problemResponse("LOT_STATE_INVALID — transition not allowed from the current qc_status"),
  },
});
route({
  method: "post",
  path: "/v1/inventory/lots/{id}/release",
  operationId: "releaseInventoryLot",
  summary: "QC release a lot — status-only counterpart of quarantine",
  permission: "inventory.quarantine",
  description:
    "Sets qc_status='released' (legal from quarantined/pending); posts nothing. Emits inventory.lot_released.",
  request: { params: IdParam, ...jsonBody(LotReleaseRequest) },
  responses: {
    ...ok("The resulting qc_status", LotQcStatusResponse),
    404: problemResponse("LOT_NOT_FOUND"),
    422: problemResponse("LOT_STATE_INVALID"),
  },
});

// ===========================================================================
// genealogy trace (spec 06 §3.7, §9.13, §5 row 11) — PHASE 2b
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/lots/{id}/trace",
  operationId: "traceInventoryLot",
  summary: "Recall trace — one query, either direction (§3.7)",
  permission: "inventory.trace.read",
  description:
    "Recursive CTE over genealogy_edges with a path-array cycle guard, depth cap 50 and a 10k-node cap → truncated:true with the boundary nodes listed (§9.13); a cycle found while reading is corrupt data and is reported, never followed. Downstream traces join terminal shipment movements; customers resolve through the Module 09 port, called after the trace transaction closes — customer_resolution is 'unavailable' while the port is unbound, 'partial' when at least one downstream shipment resolves to no customer (summary.unresolved_shipments counts them per shipment row: a movement with no module-09 shipment behind it), and 'resolved' only when every shipment named one; never a fake empty list. A shipment movement that a later reversal undid is not a downstream shipment (D-286 ruling 24). Target < 2s at 50k edges. Sales sees only customers assigned to them (§7); floor_worker holds no trace permission (403).",
  request: {
    params: IdParam,
    query: z.object({ direction: z.enum(["up", "down"]) }),
  },
  responses: {
    ...ok("Nodes, edges, summary tiles, truncation state", TraceResponse),
    404: problemResponse("LOT_NOT_FOUND"),
  },
});

// ===========================================================================
// stock units (spec 06 §2.3, §3.10.5, §5 rows 12–15, 18–19) — PHASE 2b
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/stock-units",
  operationId: "listInventoryStockUnits",
  summary: "List stock units (cursor page, newest first)",
  permission: "inventory.read",
  description:
    "parent_stock_unit_id (added 2026-08-23, D-212) answers §4.2's Genealogy children question by the DAG fact itself — the units cut from this one — and works whatever state the parent is in: a parent drained by §3.2's exhaustion flip loses its lot_id, so the previous lot_id-filter recipe had no queryable child set exactly on the unit a recall walks through.",
  request: {
    query: z.object({
      product_id: z.guid().optional(),
      kind: z.string().optional(),
      status: z.string().optional(),
      facility_id: z.guid().optional(),
      location_id: z.guid().optional(),
      lot_id: z.guid().optional(),
      parent_stock_unit_id: z.guid().optional(),
      min_remaining: z.string().optional(),
      serial_q: z.string().optional(),
      is_offcut: z.coerce.boolean().optional(),
      ...cursorQuery,
    }),
  },
  responses: ok("A page of stock units", StockUnitListResponse),
});
route({
  method: "post",
  path: "/v1/inventory/stock-units",
  operationId: "registerInventoryStockUnit",
  summary: "Register a BARE reusable container — no lot, NO movement (§3.10.5)",
  permission: "inventory.receipt.create",
  description:
    "Registers an empty container (bare bobbin/drum): status='empty', product_id NULL, measures NULL; the unit's current_location_id is the tracking state and it NEVER touches stock_levels (§2.3/§2.4). A product-carrying registration is refused with 422 UNIT_REGISTER_INVALID — filled units enter stock exclusively through POST /receipts so every quantity goes through the movement ledger. qr_payload is minted at creation (id-stable). Emits inventory.stock_unit_created.",
  request: jsonBody(StockUnitRegisterRequest),
  responses: {
    ...created("The registered container", StockUnit),
    404: problemResponse("LOCATION_NOT_FOUND"),
    409: problemResponse("SERIAL_TAKEN — UNIQUE(tenant, serial_code), tenant-global (§2.3)"),
    422: problemResponse("UNIT_REGISTER_INVALID — bare containers only on this endpoint"),
  },
});
route({
  method: "get",
  path: "/v1/inventory/stock-units/{id}",
  operationId: "getInventoryStockUnit",
  summary: "Unit card: full row + active reservation + last 5 scans",
  permission: "inventory.read",
  request: { params: IdParam },
  responses: {
    ...ok("The unit card", StockUnitCardResponse),
    404: problemResponse("UNIT_NOT_FOUND (cross-tenant ids are invisible — §9.9)"),
  },
});
route({
  method: "get",
  path: "/v1/inventory/stock-units/{id}/history",
  operationId: "getInventoryStockUnitHistory",
  summary: "Unit timeline: movements + scans merged newest-first (cursor)",
  permission: "inventory.read",
  description:
    "Cost fields on movement entries are role-redacted per §7 (null + redacted:true without the inventory.cost.read grant).",
  request: { params: IdParam, query: z.object({ ...cursorQuery }) },
  responses: {
    ...ok("A page of history entries", StockUnitHistoryResponse),
    404: problemResponse("UNIT_NOT_FOUND"),
  },
});
route({
  method: "post",
  path: "/v1/inventory/stock-units/{id}/quarantine",
  operationId: "quarantineInventoryStockUnit",
  summary: "Unit-level QC hold — in place (status-only) or relocated (one repack)",
  permission: "inventory.quarantine",
  description:
    "relocate:false (default) flips status only. relocate:true posts exactly ONE repack movement (trusted in-process flow) to the facility's quarantine location — levels follow in the same transaction (§2.4 quarantine clarifications). Only filled/in_use units can be held.",
  request: { params: IdParam, ...jsonBody(UnitQuarantineRequest) },
  responses: {
    ...ok("The resulting status + repack movement id when relocated", UnitStatusResponse),
    404: problemResponse("UNIT_NOT_FOUND"),
    422: problemResponse("UNIT_NOT_AVAILABLE — the unit's status cannot be quarantined"),
  },
});
route({
  method: "post",
  path: "/v1/inventory/stock-units/{id}/release-unit",
  operationId: "releaseInventoryStockUnit",
  summary: "Release a quarantined unit — in place, or repacked back to storage",
  permission: "inventory.quarantine",
  description:
    "to_location_id present → posts a repack quarantine → that location; absent → status flip only (§2.4).",
  request: { params: IdParam, ...jsonBody(UnitReleaseRequest) },
  responses: {
    ...ok("The resulting status + repack movement id when relocated", UnitStatusResponse),
    404: problemResponse("UNIT_NOT_FOUND / LOCATION_NOT_FOUND"),
    422: problemResponse("UNIT_NOT_AVAILABLE — only quarantined units release"),
  },
});
route({
  method: "post",
  path: "/v1/inventory/stock-units/{id}/retire",
  operationId: "retireInventoryStockUnit",
  summary: "Retire a container (drained or bare only in this slice)",
  permission: "inventory.adjust",
  description:
    "A bare container (status empty) retires with an empty-container scrap movement (quantity 1 pcs — the ledger records the container leaving circulation, §2.4); a filled/in_use unit retires only when fully drained (remaining 0 and no on-hand ledger balance) — anything else is 422 UNIT_NOT_AVAILABLE. Residual write-off travels through the dedicated cut/remeasure flows (slice 2c), never a silent retire.",
  request: { params: IdParam, ...jsonBody(UnitRetireRequest) },
  responses: {
    ...ok("The resulting status + scrap movement id for bare containers", UnitStatusResponse),
    404: problemResponse("UNIT_NOT_FOUND"),
    422: problemResponse("UNIT_NOT_AVAILABLE — the unit still holds stock or is not retirable"),
  },
});

// ===========================================================================
// measured-unit flows: cut + remeasure (spec 06 §3.2, §3.5, §5 rows 16–17) —
// PHASE 2c
// ===========================================================================

route({
  method: "post",
  path: "/v1/inventory/stock-units/{id}/cut",
  operationId: "cutInventoryStockUnit",
  summary: "Cut a measured unit into children / waste pieces (§3.2)",
  permission: "inventory.unit.cut",
  description:
    "ONE transaction: the source row is locked FOR UPDATE (§9.1 — a concurrent cut re-validates and answers 409 INSUFFICIENT_REMAINING with the fresh remaining in the problem body), the pure kernel planner (planCut) decides child lengths, serial suffixes ({parent}-{n}, smallest suffix unused TENANT-WIDE across all statuses incl. shipped/retired), kerf, the off-cut flag (product attribute full_length_threshold_m), the §3.2 step-4 tolerance write-off and the signed over-cut variance — and the service posts exactly what the plan says: per piece a cut_out plus (cut_in for children | scrap for waste, incl. the kerf and tolerance_writeoff entries; the write-off scrap carries reason_code=cut_tolerance), and the variance as an adjustment (reason remeasure) from the facility supplier (found) counter-location. Level↔measure drift from within-tolerance re-measures is reconciled in the same envelope (§3.2 drift reconciliation): a ledger shortfall against the measured remaining posts FIRST as a found-side gain adjustment, and a dying row's residual level is written off before the flip so the dead identity lands at exactly 0. The plan balances by construction: children + waste + kerf + remaining_after + variance = remaining_before, and the derived stock_levels mirror it in the same txn. reason=qc_fail creates the children status='quarantined' (lot untouched) for the Module 07 retest queue; the §7 qc cell is narrowed service-side (qc-only principals may ONLY qc_fail-cut — 403 otherwise). An exhausted returnable kind (inventory_settings.returnable_kinds) flips the SAME row to status=empty (serial retained); other kinds retire. An active reservation on the source is re-pointed to the smallest sufficient child — never onto quarantined children — else flagged reservation_not_covered; a reservation on a unit going empty/retired is auto-released (§2.6). cut_from genealogy edges + inventory.unit_cut ride the same txn.",
  request: { params: IdParam, ...jsonBody(CutRequest) },
  responses: {
    ...created("Children, waste pieces and the parent's outcome", CutResponse),
    404: problemResponse("UNIT_NOT_FOUND (cross-tenant ids are invisible — §9.9)"),
    409: problemResponse(
      "INSUFFICIENT_REMAINING — §9.1 concurrent cut (problem body carries remaining_measure) / CUT_SERIAL_TAKEN — pre-printed serial already in use / SERIAL_TAKEN",
    ),
    422: problemResponse(
      "UNIT_NOT_AVAILABLE — status not filled/in_use / CUT_NO_PIECES / CUT_PIECE_NOT_POSITIVE / CUT_SOURCE_NEGATIVE / CUT_TOLERANCE_INVALID / MEASURE_PRECISION_LOSS",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/stock-units/{id}/remeasure",
  operationId: "remeasureInventoryStockUnit",
  summary: "Correct a unit's measured remaining (§3.5 re-measure rules)",
  permission: "inventory.scan.locate",
  description:
    "FOR UPDATE on the unit. Within remeasure_tolerance_pct (default 2%, exact integer cross-multiplication, inclusive boundary): the measure is unit metadata — updated with NO movement. Beyond: a trusted-flow adjustment (reason remeasure) posts the delta — gains from the facility supplier (found) counter-location to the unit's location, losses unit location → scrap (the §3.4 step-7 counter-location rule) — and remaining_measure is set to the entered value in the same txn. The §3.9 interim manual-adjustment refusal does NOT apply (trusted in-process flow). Emits inventory.unit_remeasured.",
  request: { params: IdParam, ...jsonBody(RemeasureRequest) },
  responses: {
    ...ok("Whether an adjustment was posted + the delta", RemeasureResponse),
    404: problemResponse("UNIT_NOT_FOUND"),
    422: problemResponse("UNIT_NOT_AVAILABLE — status not filled/in_use / MEASURE_PRECISION_LOSS"),
  },
});

// ===========================================================================
// scan (spec 06 §3.5, §9.9, §5 row 20) — PHASE 2c
// ===========================================================================

route({
  method: "post",
  path: "/v1/inventory/scan",
  operationId: "postInventoryScan",
  summary: "Resolve a QR / serial and stamp last-known location (§3.5)",
  permission: "inventory.scan.locate",
  description:
    "Resolves the platform QR canon (https://{sub}.assessly.ai/q/su/{id} | /q/loc/{id}) or a bare serial, ALWAYS within the scanning session's tenant (§9.9 — a foreign tenant's QR resolves to 'unknown', reveals nothing, and the scan_events row exists only in the scanning tenant). Location attribution ladder for unit scans: (1) a location scan by the SAME device within scan_context_window_seconds (server-derived from scan_events), (2) the request's context_location_id (the floor PWA's device-bound station setting), (3) unchanged. A same-facility attribution to a different repack-legal location posts ONE trusted-flow repack of the unit's LEDGER LEVEL at the old location (never remaining_measure, which within-tolerance re-measures legally let drift — §2.4/§3.5) so stock_levels follow the unit; a cross-facility discrepancy NEVER auto-moves — it emits inventory.unit_found_offsite (Module 12 exception). Optional entered_measure routes through the §3.5 re-measure rules in the same transaction. Scan rows are append-only and idempotent on the Idempotency-Key (replay identity: payload + action + entered_measure + device_id, §9.2).",
  request: jsonBody(ScanRequest),
  responses: {
    ...created("The resolution + entity snapshot + attribution outcome", ScanResponse),
    404: problemResponse("LOCATION_NOT_FOUND — a named context_location_id that does not exist"),
    409: problemResponse("IDEMPOTENCY_KEY_REUSE — key already used by a different scan (§9.2)"),
    422: problemResponse("MEASURE_PRECISION_LOSS — malformed entered_measure"),
  },
});

// ===========================================================================
// lookup + ATP (spec 06 §3.8, §3.6, §5 rows 21, 23) — PHASE 2c
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/lookup",
  operationId: "inventoryLookup",
  summary: "The ≤3-click stock lookup (§3.8) — three ranked groups",
  permission: "inventory.read",
  description:
    "Tokenizer: NNmm2/mm² → product DIMENSION filter — the attribute key is resolved per tenant from the INSTALLED PACK (the attribute measured in mm2; the cable-wire pack calls it cross_section_mm2), never a key literal in module 06 (doctrine 5), and a pack that measures nothing in mm2 answers honestly empty rather than dropping the constraint; NNm/NNkg → required measure; residual text → product name/SKU match. Group 1: single units (filled/in_use, released lots) with remaining ≥ required, ranked smallest-sufficient-first (SQL ORDER BY — minimizes stranded off-cuts), reserved units flagged with their demand ref. Group 2: off-cut COMBOS over eligible is_offcut units via the shared kernel matcher (D-047 pt 5 — computed locally; a PAIR always stands in one facility even without a facility filter; discount hints null with discount_source 'unavailable' while Module 05 is unbound), with offcut_eligible_count + offcut_rejected[] {stock_unit_id, reason} riding the response — quarantine-flavoured rejection ids are null for callers without the §7 visibility grants. Group 3: bulk lot availability. Quarantined/pending stock is hidden except for callers holding inventory.quarantine or inventory.count.review (§7). NO BOUND IS SILENT: product resolution runs over the WHOLE set of products the tokens name (never one page of the product list), and when that set overflows the 2 000 bound it is re-resolved ONCE over the products carrying stock in the caller's scope (D-298) — so `product_resolution: false` means the whole set that could have answered was searched, never merely the newest page of it, and `truncated` states every cap that bit — product_resolution (more products matched than were searched, so stock is missing from every group), unit_matches / bulk_lots (the 50-row group caps) and offcut_candidates (the 500-piece candidate scan, which also bounds what offcut_eligible_count counted). All four flags are always present; all false means an empty group may be read as 'there is none'. p95 server budget 800ms.",
  request: {
    query: z.object({
      q: z.string().max(200).optional(),
      product_query: z.string().max(200).optional(),
      required_measure: z.string().optional(),
      uom: z.string().optional(),
      facility_id: z.guid().optional(),
      include_reserved: z.coerce.boolean().optional(),
    }),
  },
  responses: ok("Ranked unit matches, off-cut combos and bulk lots", LookupResponse),
});
route({
  method: "get",
  path: "/v1/inventory/atp",
  operationId: "getInventoryAtp",
  summary: "ATP with itemized terms — always honest (§3.6)",
  permission: "inventory.read",
  description:
    "Real terms are computed live: on_hand (bin/receiving/shipping, released lots, unit not quarantined), reserved (active, unexpired), in_transit_inbound (dispatched transfer lines to this facility). Terms owned by unbuilt/unbound modules — po_inbound (10), wo_planned_output/wo_demand (04) — report qty_base:null with unavailable:true + owner_module, NEVER a fake 0; atp_qty totals the available terms only and available_terms_only says so (§3.6 amendment, 2c).",
  request: {
    query: z.object({
      product_id: z.guid(),
      facility_id: z.guid(),
      date: z.string().optional(),
    }),
  },
  responses: {
    ...ok("The ATP quantity + itemized terms", AtpResponse),
    404: problemResponse("PRODUCT_NOT_FOUND / FACILITY_NOT_FOUND"),
  },
});

// ===========================================================================
// reservations (spec 06 §2.6, §5 rows 28–30) — PHASE 2d
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/reservations",
  operationId: "listInventoryReservations",
  summary: "List reservations (cursor page, newest first)",
  permission: "inventory.reservation.manage",
  description:
    "Soft allocations that reduce ATP without moving stock (§2.6). Facility-scoped roles see their facilities' reservations only (D-043 service-layer narrowing).",
  request: {
    query: z.object({
      product_id: z.guid().optional(),
      demand_type: z.string().optional(),
      demand_id: z.guid().optional(),
      status: z.string().optional(),
      ...cursorQuery,
    }),
  },
  responses: ok("A page of reservations", ReservationListResponse),
});
route({
  method: "post",
  path: "/v1/inventory/reservations",
  operationId: "createInventoryReservation",
  summary: "Reserve stock — a quantity, a lot, or a specific unit (§2.6)",
  permission: "inventory.reservation.manage",
  description:
    "Quantity reservations validate against the LIVE §3.6 terms this module owns (on_hand − active unexpired reservations + in_transit inbound; the expires_at filter is live, so a stale quote_hold never blocks promising between sweeps) under a per-(product, facility) advisory lock — over ATP is 409 INSUFFICIENT_ATP (available_qty_base in the problem extensions) unless allow_backorder. Unit reservations lock the unit (§9.1), require status filled/in_use (422 UNIT_NOT_AVAILABLE) on a RELEASED lot (422 LOT_NOT_RELEASED) with ledger balance ≥ qty; the §2.6 one-active-hold-per-unit partial UNIQUE surfaces a race as 409 RESERVATION_CONFLICT. demand_type is limited to the USER-mintable kinds — `transfer_line` holds are system-managed by §3.3 dispatch (422 RESERVATION_INVALID service-side; the enum refuses it at the contract). quote_hold defaults expires_at to now + the tenant's quote_hold_expiry_hours (48h, §2.13). NAMING THE DEMAND (D-151): sales_order_line / work_order_component / maintenance_order_part REQUIRE demand_id — it names a row in the module that owns the demand and that module mints it (module 04 mints its component holds at release), so a demand_ref alone is 422 RESERVATION_INVALID naming that module. quote_hold accepts EITHER: it points at nothing until module 09 lands, so demand_ref (the words the user typed) is accepted alone and demand_id is DERIVED from it — uuid v5 over (tenant, normalized reference), so the same reference always resolves to the same demand and every grouping surface (the (demand_type, demand_id) index, the fulfil/release seams, GET /reservations?demand_id=) keeps working. Emits inventory.reservation_created.",
  request: jsonBody(ReservationCreateRequest),
  responses: {
    ...created("The created reservation", ReservationCreateResponse),
    404: problemResponse("PRODUCT_NOT_FOUND / FACILITY_NOT_FOUND / LOT_NOT_FOUND / UNIT_NOT_FOUND"),
    409: problemResponse(
      "INSUFFICIENT_ATP — over available-to-promise without allow_backorder / RESERVATION_CONFLICT — the unit already carries an active hold (§2.6)",
    ),
    422: problemResponse(
      "RESERVATION_INVALID — system-managed demand kind, non-positive quantity, or a demand this caller cannot name (a module-owned kind with no demand_id, or a quote_hold with neither demand_id nor demand_ref) / UNIT_NOT_AVAILABLE / LOT_NOT_RELEASED — held stock cannot be promised (§3.1) / LOT_STATE_INVALID / UOM_NOT_CONVERTIBLE / MEASURE_PRECISION_LOSS",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/reservations/{id}/release",
  operationId: "releaseInventoryReservation",
  summary: "Release an active reservation (§2.6 active → released)",
  permission: "inventory.reservation.manage",
  description:
    "Emits inventory.reservation_released. Releasing an already released/expired reservation is an idempotent no-op; a FULFILLED one is 409 RESERVATION_CONFLICT (the stock was consumed by its demand — there is nothing to free). An ACTIVE `transfer_line` hold is 409 RESERVATION_CONFLICT too — it is system-managed by the §3.3 flows: receive or cancel the transfer instead (releasing it mid-transit would void the atomic active→fulfilled bookkeeping).",
  request: { params: IdParam },
  responses: {
    ...ok("The released reservation", ReservationReleaseResponse),
    404: problemResponse("RESERVATION_NOT_FOUND (cross-tenant ids are invisible — §9.9)"),
    409: problemResponse(
      "RESERVATION_CONFLICT — the reservation was already fulfilled, or is a system-managed transfer_line hold",
    ),
  },
});

// ===========================================================================
// transfers (spec 06 §2.7, §3.3, §5 rows 31–36) — PHASE 2d
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/transfers",
  operationId: "listInventoryTransfers",
  summary: "List transfers (cursor page, newest first)",
  permission: "inventory.read",
  description:
    "Transfers span facilities BY DESIGN (§2.7): facility-scoped roles see every transfer touching one of their facilities (either endpoint — in-transit stock is visible at both ends).",
  request: {
    query: z.object({
      status: z.string().optional(),
      from_facility_id: z.guid().optional(),
      to_facility_id: z.guid().optional(),
      ...cursorQuery,
    }),
  },
  responses: ok("A page of transfers", TransferListResponse),
});
route({
  method: "post",
  path: "/v1/inventory/transfers",
  operationId: "createInventoryTransfer",
  summary: "Create a draft transfer with lines (§3.3 — rows only, no stock effect)",
  permission: "inventory.transfer.manage",
  description:
    "The transfer_code is minted TR-{yyyy}-{00000} from a per-tenant sequence under an advisory lock (§2.7; the year is the UTC calendar year at creation). from/to locations default to the origin facility's shipping and the destination's receiving virtual locations. Lines: a STOCK-UNIT line (the unit's whole quantity — a container never half-departs; empty-container lines carry product NULL, quantity 1, uom pcs), or a BULK lot × qty line drawn from the transfer's from_location. Draft validation: on-hand at the source ≥ qty (409 INSUFFICIENT_ATP with the available quantity), and the §3.1 QC gate (D-211) — a line naming a pending/quarantined/rejected lot (bulk via lot_id, unit via the unit's own lot) is 422 LOT_NOT_RELEASED with the lot and its qc_status in the extensions; re-validated at dispatch. Planner/procurement hold create (and creator-edit/cancel) only — dispatch is denied service-side (§7 narrowing).",
  request: jsonBody(TransferCreateRequest),
  responses: {
    ...created("The created draft with its lines", TransferDetailResponse),
    404: problemResponse(
      "FACILITY_NOT_FOUND / LOCATION_NOT_FOUND / UNIT_NOT_FOUND / LOT_NOT_FOUND",
    ),
    409: problemResponse("INSUFFICIENT_ATP — on-hand at the source does not cover a line"),
    422: problemResponse(
      "UNIT_NOT_AVAILABLE — unit status/facility forbids transfer / TRANSFER_STATE_INVALID — malformed line (no product or unit; duplicate unit) / LOT_NOT_RELEASED — a line names a held lot (§3.1 QC gate, D-211) / UOM_NOT_CONVERTIBLE / MEASURE_PRECISION_LOSS",
    ),
  },
});
route({
  method: "get",
  path: "/v1/inventory/transfers/{id}",
  operationId: "getInventoryTransfer",
  summary: "Transfer detail: header + lines",
  permission: "inventory.read",
  request: { params: IdParam },
  responses: {
    ...ok("The transfer with its lines", TransferDetailResponse),
    404: problemResponse("TRANSFER_NOT_FOUND (cross-tenant ids are invisible — §9.9)"),
  },
});
route({
  method: "patch",
  path: "/v1/inventory/transfers/{id}",
  operationId: "patchInventoryTransfer",
  summary: "Edit a DRAFT transfer (header fields; full line-set replacement)",
  permission: "inventory.transfer.manage",
  description:
    "Draft only — any other state is 409 TRANSFER_STATE_INVALID. §5 row 33 principals: the creator, storekeeper, or an admin (service-enforced). `lines`, when present, REPLACES the whole line set (re-validated like create).",
  request: { params: IdParam, ...jsonBody(TransferPatchRequest) },
  responses: {
    ...ok("The updated draft", TransferDetailResponse),
    404: problemResponse("TRANSFER_NOT_FOUND"),
    409: problemResponse("TRANSFER_STATE_INVALID — not a draft / INSUFFICIENT_ATP"),
    422: problemResponse(
      "UNIT_NOT_AVAILABLE / TRANSFER_STATE_INVALID — malformed line / LOT_NOT_RELEASED — a line names a held lot (§3.1 QC gate, D-211)",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/transfers/{id}/dispatch",
  operationId: "dispatchInventoryTransfer",
  summary: "Dispatch: stock leaves for the origin facility's in_transit location (§3.3)",
  permission: "inventory.transfer.manage",
  description:
    "ONE transaction, header locked FOR UPDATE (a concurrent double-dispatch is exactly one 409 TRANSFER_STATE_INVALID; a same-key §9.2 retry of an already-committed dispatch replays the current detail instead — the first leg's ledger idempotency key is the in-domain identity). Per line: a transfer_out movement through the single ledger write path — unit lines move the unit's WHOLE ledger balance from its current location (mismatch after a cut/re-measure is 422 UNIT_NOT_AVAILABLE with the fresh ledger_qty_base), bulk lines move lot × qty from the transfer's from_location; the unit's status flips to in_transit; a transfer_line reservation is auto-created against the in-transit stock (facility = destination); a unit already carrying an ACTIVE reservation refuses with 409 RESERVATION_CONFLICT (release or fulfil the demand first — §2.6 one-hold rule); the §3.1 QC gate is RE-VALIDATED per line (D-211 — a lot quarantined since draft is 422 LOT_NOT_RELEASED and nothing leaves). Storekeeper/admin only (planner/procurement are create-only, §7). Emits inventory.transfer_dispatched + a reservation_created per auto-hold.",
  request: { params: IdParam },
  responses: {
    ...ok("The dispatched transfer", TransferDetailResponse),
    404: problemResponse("TRANSFER_NOT_FOUND"),
    409: problemResponse(
      "TRANSFER_STATE_INVALID — not a draft (incl. the concurrent-dispatch loser) / RESERVATION_CONFLICT — a unit line is actively reserved / NEGATIVE_STOCK_BLOCKED",
    ),
    422: problemResponse(
      "UNIT_NOT_AVAILABLE / MOVEMENT_ILLEGAL — source location kind (§2.4) / LOT_NOT_RELEASED — a line's lot was held since draft (§3.1 QC gate re-validated at dispatch, D-211)",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/transfers/{id}/receive",
  operationId: "receiveInventoryTransfer",
  summary: "Receive (full or partial) at the destination (§3.3)",
  permission: "inventory.transfer.receive",
  description:
    "ONE transaction. Per named line: a transfer_in movement (origin in_transit → the destination location — the one legal cross-facility movement, §2.4); stock_unit.facility_id updates and status returns to filled (empty for bare-container lines — how a returned bare bobbin changes facility, with NO stock_levels row); received_qty_base accumulates; the line's transfer_line reservation is FULFILLED atomically (§2.6 — ATP never double-subtracts). A line left SHORT requires variance_reason (422 TRANSFER_VARIANCE_REASON_REQUIRED) — the shortfall stays in_transit (§9.10) and inventory.transfer_receive_variance carries it valued at the last captured unit cost of the LINE'S OWN lot (null when unvalued — never another lot's price, edge case 14). Unit lines receive in FULL or not at all (receive whole, then re-measure — ruled 2d); over-receiving past a line's dispatched quantity is 422 TRANSFER_STATE_INVALID (found excess is a §3.9 adjustment); a line_id may appear at most once per call (422 TRANSFER_STATE_INVALID). §9.2: a same-key retry of an already-committed receive replays per line (the per-line ledger idempotency keys are probed BEFORE the state gates) without re-applying stock or re-emitting events. Status: partially_received until every line is covered, then received. Emits inventory.transfer_received.",
  request: { params: IdParam, ...jsonBody(TransferReceiveRequest) },
  responses: {
    ...ok("The transfer after this receive", TransferReceiveResponse),
    404: problemResponse("TRANSFER_NOT_FOUND / TRANSFER_LINE_NOT_FOUND / LOCATION_NOT_FOUND"),
    409: problemResponse("TRANSFER_STATE_INVALID — not dispatched/partially_received"),
    422: problemResponse(
      "TRANSFER_VARIANCE_REASON_REQUIRED — short line without a reason / TRANSFER_STATE_INVALID — over-receive or partial unit receive / MEASURE_PRECISION_LOSS",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/transfers/{id}/cancel",
  operationId: "cancelInventoryTransfer",
  summary: "Cancel a draft, or a dispatched transfer with zero lines received (§3.3)",
  permission: "inventory.transfer.manage",
  description:
    "Draft cancel is rows-only. A DISPATCHED cancel (only while zero lines are received) returns the stock from in_transit to its origin locations by REVERSING the dispatch legs (§3.3 'via reversal movements'), restores unit statuses (filled / empty) and releases every auto transfer_line reservation with its §6 event — a reservation never dangles. Principals: the transfer's creator or an admin (§3.3). Manual reversal of transfer legs through POST /movements/{id}/reverse is blocked (422 REVERSAL_NOT_REVERSIBLE — §3.9 amendment, 2d): only this flow may unwind a dispatch. §9.2: a same-key retry of an already-committed DISPATCHED-cancel replays via the reversal legs' ledger keys; a draft cancel writes no movements, so its retry identity is the platform idempotency layer alone.",
  request: { params: IdParam, ...jsonBody(TransferCancelRequest) },
  responses: {
    ...ok("The cancelled transfer", TransferDetailResponse),
    404: problemResponse("TRANSFER_NOT_FOUND"),
    409: problemResponse("TRANSFER_STATE_INVALID — lines already received, or a terminal state"),
  },
});

// ===========================================================================
// receipts (spec 06 §3.1, §3.10, §5 row 27) — PHASE 2b
// ===========================================================================

route({
  method: "post",
  path: "/v1/inventory/receipts",
  operationId: "createInventoryReceipt",
  summary: "Composite receipt: lot + units + movements, ONE transaction",
  permission: "inventory.receipt.create",
  description:
    "One transaction: create (or reuse) the lot → create N stock units with per-unit initial/claimed measures → post N receipt movements supplier → receiving/bin/quarantine through the single ledger write path (levels update in the same txn) → add consumed_into/reprocessed_into genealogy edges for parent_refs[] (§3.7 regrind) → emit inventory.lot_created / stock_unit_created / movement_recorded. Any failure rolls the WHOLE receipt back — no partial lots or phantom units. Bulk stock (no units[]) posts one lot-level movement and lives as lot × location in stock_levels with no stock unit. unit_cost_minor is captured when provided (read back cost-redacted per §7).",
  request: jsonBody(ReceiptCreateRequest),
  responses: {
    ...created("The created lot, unit ids and movement ids", ReceiptCreateResponse),
    404: problemResponse("PRODUCT_NOT_FOUND / LOCATION_NOT_FOUND / LOT_NOT_FOUND"),
    409: problemResponse("SERIAL_TAKEN / LOT_CODE_TAKEN / NEGATIVE_STOCK_BLOCKED"),
    422: problemResponse(
      "MOVEMENT_ILLEGAL (§2.4 legality) / UOM_NOT_CONVERTIBLE / MEASURE_PRECISION_LOSS / LOT_STATE_INVALID / UNIT_REGISTER_INVALID / PRODUCT_TRACKING_MISMATCH — D-207: the receipt's shape contradicts the product's §2 `tracking` grain. A `measured_unit` or `serial` material is received as units[] (its containers ARE its stock); a `none` material is received in bulk (it has no container identities to mint); a `lot` material is legal either way. Checked BEFORE the lot is created, so a refusal leaves nothing behind, and before module 07's receiving seam, so a grain error is reported as the catalogue error it is rather than as an unreadable AQL lot size / GENEALOGY_CYCLE / GENEALOGY_DEPTH_CAP / QUALITY_SAMPLING_PLAN_INVALID — module 07's receiving sampler (spec 07 §3.3, D-194): a BULK receipt (no units[]) met a receiving control point whose plan is `aql`, and ISO 2859-1 has no reading for a lot size in kilograms; receive it as serialized units or change the point's plan / QUALITY_AQL_PLAN_UNCONFIRMED — module 07 declines to state an accept number for this lot size (spec 07 §3.3, D-197: fifteen of the ninety-six MIL-STD-105E cells are unconfirmed); set the point to `fixed_count` or have quality confirm the cell against the standard. **Both are QUALITY codes raised on an INVENTORY route by ruling, not by accident** — the receiving gate is a synchronous port inside this receipt's transaction (D-187), so a quality refusal refuses the receipt, and the storekeeper standing at the loading bay is exactly who can act on it",
    ),
  },
});

// ===========================================================================
// cycle counts (spec 06 §2.8, §3.4, §5 rows 37–46) — PHASE 2e
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/cycle-counts",
  operationId: "listInventoryCycleCounts",
  summary: "List cycle counts (cursor page, newest first)",
  permission: "inventory.read",
  description:
    "variance_value_minor is field-redacted (null + redacted:true) without the inventory.cost.read grant (spec 06 §7).",
  request: {
    query: z.object({
      status: z.string().optional(),
      facility_id: z.guid().optional(),
      schedule_id: z.guid().optional(),
      ...cursorQuery,
    }),
  },
  responses: ok("A page of cycle counts", CycleCountListResponse),
});
route({
  method: "post",
  path: "/v1/inventory/cycle-counts",
  operationId: "createInventoryCycleCount",
  summary: "Create an ad-hoc cycle count (draft)",
  permission: "inventory.count.perform",
  description:
    "Scope = the location's subtree (§3.4 step 1). The scope location must be countable — virtual counter-locations (is_countable=false) answer 422 LOCATION_NOT_COUNTABLE. floor_worker holds inventory.count.perform for RECORDING only (§7 'count only', service-enforced): create/start/submit require storekeeper or an admin. product_family_id is refused 422 until Module 01 ships product families (never silently ignored — a wider snapshot than asked for is worse than a refusal).",
  request: jsonBody(CycleCountCreateRequest),
  responses: {
    ...created("The draft count", CycleCountDetailResponse),
    404: problemResponse("LOCATION_NOT_FOUND"),
    422: problemResponse("LOCATION_NOT_COUNTABLE / VALIDATION_FAILED"),
  },
});
route({
  method: "get",
  path: "/v1/inventory/cycle-counts/{id}",
  operationId: "getInventoryCycleCount",
  summary: "Count detail + worksheet lines",
  permission: "inventory.read",
  description:
    "BLIND ENFORCEMENT IS SERVER-SIDE (§3.4 step 3, §7): while the count is blind AND in_progress, expected_qty_base is OMITTED from every line (the field is absent, not null) for any caller without inventory.count.review — even group_admin. A reviewer reading expected values on an in-progress blind count is recorded in the audit chain (§9.12). Variance money is cost-redacted per §7.",
  request: { params: IdParam },
  responses: {
    ...ok("The count with its lines", CycleCountDetailResponse),
    404: problemResponse("COUNT_NOT_FOUND (§9.9 — cross-tenant/out-of-scope ids invisible)"),
  },
});
route({
  method: "post",
  path: "/v1/inventory/cycle-counts/{id}/start",
  operationId: "startInventoryCycleCount",
  summary: "Snapshot expected quantities + open the count",
  permission: "inventory.count.perform",
  description:
    "draft → in_progress (§3.4 step 2): snapshots every stock_levels row in the scope subtree (countable locations only) into cycle_count_lines — one line per level row, including stock units with their expected remaining — and sets snapshot_at. Movements in scoped locations are NOT blocked; §9.11 handles drift at posting. Not storekeeper/admin → 403 (floor_worker records only).",
  request: { params: IdParam },
  responses: {
    ...ok("The opened count", CycleCountDetailResponse),
    404: problemResponse("COUNT_NOT_FOUND"),
    409: problemResponse("COUNT_STATE_INVALID — the count is not draft"),
  },
});
route({
  method: "put",
  path: "/v1/inventory/cycle-counts/{id}/lines/{lineId}/count",
  operationId: "recordInventoryCountLine",
  summary: "Record a counted quantity / re-measured length (floor)",
  permission: "inventory.count.perform",
  description:
    "in_progress only. counted_qty_base for bulk lines; counted_measure for stock-unit lines (must be denominated in the line's UoM). A note WITHOUT a quantity marks the line explicitly skipped (§3.4 step 4 — it drops from posting at submit). Offline-idempotent: re-recording the same values is a no-op; a correction overwrites while the count is still in_progress. Blind counts answer WITHOUT expected_qty_base.",
  request: {
    params: z.object({ id: z.guid(), lineId: z.guid() }),
    ...jsonBody(CountLineCountRequest),
  },
  responses: {
    ...ok("The recorded line (blind-stripped for non-reviewers)", CycleCountDetailResponse),
    404: problemResponse("COUNT_NOT_FOUND / COUNT_LINE_NOT_FOUND"),
    409: problemResponse("COUNT_STATE_INVALID — the count is not in_progress"),
    422: problemResponse(
      "COUNT_STATE_INVALID — a measure on a bulk line, a UoM mismatch, or an empty record",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/cycle-counts/{id}/lines",
  operationId: "addInventoryCountLine",
  summary: "Add a found-but-unexpected item to the worksheet",
  permission: "inventory.count.perform",
  description:
    "§3.4 step 3: an uncounted-but-present unit (or bulk product) found in a scoped bin is added as a new line with expected 0. The location must lie inside the count's scope subtree and be countable. A line already on the worksheet answers 409.",
  request: { params: IdParam, ...jsonBody(CountLineAddRequest) },
  responses: {
    ...created("The count with the added line", CycleCountDetailResponse),
    404: problemResponse(
      "COUNT_NOT_FOUND / LOCATION_NOT_FOUND / PRODUCT_NOT_FOUND / UNIT_NOT_FOUND",
    ),
    409: problemResponse("COUNT_STATE_INVALID — the identity is already on the worksheet"),
    422: problemResponse(
      "LOCATION_NOT_COUNTABLE / COUNT_STATE_INVALID — outside the scope subtree",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/cycle-counts/{id}/submit",
  operationId: "submitInventoryCycleCount",
  summary: "Close counting → variances computed → review",
  permission: "inventory.count.perform",
  description:
    "in_progress → review (§3.4 step 4): every line must be counted or explicitly skipped with a note (skipped lines flip to rejected and drop from posting). Variances = counted − expected, valued at the last captured unit cost per lot (lot-scoped, D-048 pt 9 — an uncosted lot is UNVALUED, never borrowed-priced; any unvalued variance routes the count to the TOP approval tier, §9.14). Emits inventory.count_submitted. Not storekeeper/admin → 403.",
  request: { params: IdParam },
  responses: {
    ...ok("The count in review, variances computed", CycleCountDetailResponse),
    404: problemResponse("COUNT_NOT_FOUND"),
    409: problemResponse("COUNT_STATE_INVALID — not in_progress"),
    422: problemResponse("COUNT_STATE_INVALID — uncounted lines without a skip note"),
  },
});
route({
  method: "post",
  path: "/v1/inventory/cycle-counts/{id}/lines/{lineId}/review",
  operationId: "reviewInventoryCountLine",
  summary: "Accept / reject / request recount for a line",
  permission: "inventory.count.review",
  description:
    "review status only (§3.4 step 5). reject requires a note (the line drops from posting). recount spawns a FRESH blind line (recount_of_line_id linkage, expected = the CURRENT level) and returns the count to in_progress until recounts are done.",
  request: {
    params: z.object({ id: z.guid(), lineId: z.guid() }),
    ...jsonBody(CountLineReviewRequest),
  },
  responses: {
    ...ok("The count after the review action", CycleCountDetailResponse),
    404: problemResponse("COUNT_NOT_FOUND / COUNT_LINE_NOT_FOUND"),
    409: problemResponse("COUNT_STATE_INVALID — not in review, or the line is not reviewable"),
    422: problemResponse("COUNT_STATE_INVALID — reject without a note"),
  },
});
route({
  method: "post",
  path: "/v1/inventory/cycle-counts/{id}/approve",
  operationId: "approveInventoryCycleCount",
  summary: "Approve (local tier) → post the count adjustments",
  permission: "inventory.count.review",
  description:
    "§3.4 steps 6–7 under the D-049 LOCAL approval tier while Module 12's APPROVAL_CHAIN_PORT is unbound: required tier = auto (≤ count_auto_approve_minor — any reviewer), facility (≤ facility_approve_threshold_minor — facility_admin), else group_admin; UNVALUED variance lines, a currency mismatch, or UNCONFIGURED tiers force the TOP tier (§9.14 — unknown value = treat as large; never auto-approve, never fabricate a chain). A sufficient caller posts in the same transaction: per accepted line the variance is recomputed against a FRESH level read (§9.11) — a line whose level moved since the snapshot flips to recount (moved_during_count) and the count returns to in_progress with NOTHING posted; otherwise losses post location → scrap (reason count_variance) and gains post the facility supplier virtual → location (reason found) as count_adjustment movements (§3.4 step 7), measured units get remaining_measure := counted_measure, and inventory.count_posted is emitted. An insufficient caller in review escalates the count to pending_approval (posted:false, escalated:true).",
  request: { params: IdParam },
  responses: {
    ...ok("Posted, escalated, or returned for recounts", CountApproveResponse),
    404: problemResponse("COUNT_NOT_FOUND"),
    409: problemResponse(
      "COUNT_STATE_INVALID — not in review/pending_approval, or unreviewed lines remain",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/cycle-counts/{id}/cancel",
  operationId: "cancelInventoryCycleCount",
  summary: "Cancel a count (any pre-posted state)",
  permission: "inventory.count.review",
  description: "facility_admin/group_admin only (§5 row 45, service-enforced). posted is final.",
  request: { params: IdParam, ...jsonBody(CountCancelRequest) },
  responses: {
    ...ok("The cancelled count", CycleCountDetailResponse),
    404: problemResponse("COUNT_NOT_FOUND"),
    409: problemResponse("COUNT_STATE_INVALID — already posted or cancelled"),
  },
});

// ===========================================================================
// count schedules (spec 06 §2.8, §9.8, §5 row 46) — PHASE 2e
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/count-schedules",
  operationId: "listInventoryCountSchedules",
  summary: "List cycle count schedules",
  permission: "inventory.count_schedule.manage",
  request: {
    query: z.object({
      facility_id: z.guid().optional(),
      is_active: z.coerce.boolean().optional(),
      ...cursorQuery,
    }),
  },
  responses: ok("A page of schedules", CountScheduleListResponse),
});
route({
  method: "post",
  path: "/v1/inventory/count-schedules",
  operationId: "createInventoryCountSchedule",
  summary: "Create a count schedule",
  permission: "inventory.count_schedule.manage",
  description:
    "next_run_on is a business date interpreted in the FACILITY's timezone (§9.8): the in-process materializer opens a draft count when the facility's local date reaches it, then advances it by the frequency. product_family_id is refused 422 until Module 01 ships product families.",
  request: jsonBody(CountScheduleCreateRequest),
  responses: {
    ...created("The schedule", CycleCountSchedule),
    404: problemResponse("FACILITY_NOT_FOUND / LOCATION_NOT_FOUND"),
    422: problemResponse("LOCATION_NOT_COUNTABLE / VALIDATION_FAILED"),
  },
});
route({
  method: "patch",
  path: "/v1/inventory/count-schedules/{id}",
  operationId: "patchInventoryCountSchedule",
  summary: "Edit / activate / deactivate a schedule",
  permission: "inventory.count_schedule.manage",
  request: { params: IdParam, ...jsonBody(CountSchedulePatchRequest) },
  responses: {
    ...ok("The updated schedule", CycleCountSchedule),
    404: problemResponse("SCHEDULE_NOT_FOUND"),
  },
});

// ===========================================================================
// adjustment requests (spec 06 §2.12, §3.9, §5 row 48) — PHASE 2e
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/adjustment-requests",
  operationId: "listInventoryAdjustmentRequests",
  summary: "List adjustment requests (pending + settled)",
  permission: "inventory.read",
  description:
    "est_value_minor/currency are field-redacted (null + redacted:true) without the inventory.cost.read grant (§7 — the same money rule as movements).",
  request: {
    query: z.object({
      status: z.string().optional(),
      facility_id: z.guid().optional(),
      requested_by: z
        .guid()
        .optional()
        .describe(
          "Only the requests this user raised (D-328), so a list of one's own requests pages honestly.",
        ),
      ref_work_order_id: z
        .guid()
        .optional()
        .describe(
          "Only the requests recorded against this work order (spec 06 §5 row 48, D-391 ruling 3).",
        ),
      ...cursorQuery,
    }),
  },
  responses: ok("A page of adjustment requests", AdjustmentRequestListResponse),
});
route({
  method: "get",
  path: "/v1/inventory/adjustment-requests/{id}",
  operationId: "getInventoryAdjustmentRequest",
  summary: "Adjustment request detail (incl. the held movement payload)",
  permission: "inventory.read",
  request: { params: IdParam },
  responses: {
    ...ok("The request", AdjustmentRequestDetailResponse),
    404: problemResponse("ADJUSTMENT_REQUEST_NOT_FOUND"),
  },
});
route({
  method: "post",
  path: "/v1/inventory/adjustment-requests",
  operationId: "createInventoryAdjustmentRequest",
  summary: "Deliberately route a manual adjustment to approval",
  permission: "inventory.adjust",
  description:
    "The explicit sibling of the POST /movements value gate (§3.9 amendment, 2e): holds the exact validated movement payload for approval REGARDLESS of the threshold — for the operator who knows the correction needs a second pair of eyes. Nothing touches the ledger until an approve decide. Same 202 body as the gate's over-threshold branch.",
  request: jsonBody(AdjustmentRequestCreateRequest),
  responses: {
    202: {
      description: "Held for approval",
      content: { "application/json": { schema: AdjustmentRequestAcceptedResponse } },
    },
    404: problemResponse(
      "LOCATION_NOT_FOUND / PRODUCT_NOT_FOUND / LOT_NOT_FOUND / WORK_ORDER_NOT_FOUND — the `work_order_id` names no work order the caller can read, or module 04 is not deployed (spec 06 §3.9, D-391 ruling 3)",
    ),
    409: problemResponse("IDEMPOTENCY_KEY_REUSE (§9.2)"),
    422: problemResponse(
      "MOVEMENT_ILLEGAL — §2.4 legality, validated at request time AND re-validated at posting",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/adjustment-requests/{id}/decide",
  operationId: "decideInventoryAdjustmentRequest",
  summary: "Approve or reject a held adjustment (local tier)",
  permission: "inventory.count.review",
  description:
    "The D-049 LOCAL approval tier while Module 12's APPROVAL_CHAIN_PORT is unbound (the §3.9 'same chain slot as count variances'): est_value ≤ facility_approve_threshold_minor → facility_admin may decide; above, UNVALUED (edge case 14), a currency mismatch, or unconfigured tiers → group_admin only. Approve re-validates the held payload against FRESH stock and posts the movement (trusted flow, stamped source_doc_type='manual_adjustment' + the request id); if the stock changed since the request and posting would violate an invariant, the request flips to rejected (reject_reason='stale_stock') and the response is 409 ADJUSTMENT_REQUEST_STALE_STOCK. Reject requires a note. Resolution is recorded on the row (decided_by/resolved_at) and audit-chained.",
  request: { params: IdParam, ...jsonBody(AdjustmentRequestDecideRequest) },
  responses: {
    ...ok(
      "The settled request (posted_movement_id set on approve)",
      AdjustmentRequestDetailResponse,
    ),
    404: problemResponse("ADJUSTMENT_REQUEST_NOT_FOUND"),
    400: problemResponse("VALIDATION_FAILED — reject without a note"),
    409: problemResponse(
      "ADJUSTMENT_REQUEST_STATE_INVALID — already settled / ADJUSTMENT_REQUEST_STALE_STOCK — stock changed since the request; flipped to rejected",
    ),
  },
});
route({
  method: "post",
  path: "/v1/inventory/adjustment-requests/{id}/cancel",
  operationId: "cancelInventoryAdjustmentRequest",
  summary: "Cancel a pending adjustment request",
  permission: "inventory.adjust",
  description: "The requester or a facility_admin/group_admin, while pending (§3.9).",
  request: { params: IdParam, ...jsonBody(AdjustmentRequestCancelRequest) },
  responses: {
    ...ok("The cancelled request", AdjustmentRequestDetailResponse),
    404: problemResponse("ADJUSTMENT_REQUEST_NOT_FOUND"),
    409: problemResponse("ADJUSTMENT_REQUEST_STATE_INVALID — already settled"),
  },
});

// ===========================================================================
// inventory settings (spec 06 §2.13, §5 row 49) — PHASE 2e
// ===========================================================================

route({
  method: "get",
  path: "/v1/inventory/settings",
  operationId: "getInventorySettings",
  summary: "The tenant's inventory operating thresholds",
  permission: "inventory.adjust",
  description:
    "Admin surface (§5 row 49, service-enforced: facility_admin/group_admin read; the D-040 two-layer pattern). A tenant with no row yet answers the §2.13 column defaults.",
  responses: ok("The settings", InventorySettingsResponse),
});
route({
  method: "patch",
  path: "/v1/inventory/settings",
  operationId: "patchInventorySettings",
  summary: "Update inventory settings (group_admin)",
  permission: "inventory.adjust",
  description:
    "group_admin ONLY (service-enforced). Money knobs travel with their currency (the §2.13 CHECK pairs — a bare value or bare currency is 422). The §3.9 gate, the D-049 approval tiers and the sweepers read these live.",
  request: jsonBody(InventorySettingsPatchRequest),
  responses: {
    ...ok("The updated settings", InventorySettingsResponse),
    422: problemResponse("VALIDATION_FAILED — an unpaired money knob/currency"),
  },
});
